/**
 * Application Digital Copyright & Security Utilities
 */

export const AUTHOR_NAME = 'Variable Sticker Studio';
export const COPYRIGHT_YEAR = '2026';
export const APP_NAME = 'Variable Sticker Studio';
export const FULL_COPYRIGHT_TEXT = '© 2026 Variable Sticker Studio. All rights reserved.';
export const SHORT_COPYRIGHT_NOTICE = 'All rights reserved. Unauthorized reproduction or redistribution is prohibited.';
export const SECURITY_SIGNATURE_ID = 'VSS-2026-VERIFIED-INTEGRITY';


/**
 * Decodes the copyright signature
 */
export function getDecodedCopyrightSignature(): string {
  return FULL_COPYRIGHT_TEXT;
}

/**
 * Validates integrity of copyright metadata
 */
export function verifyCopyrightIntegrity(text: string): boolean {
  if (!text) return false;
  return text.includes('2026') && text.includes('Variable Sticker Studio');
}

/**
 * Paints the copyright directly into an HTML5 Canvas as raster pixels.
 */
export function renderCanvasWatermark(canvas: HTMLCanvasElement, width = 640, height = 70): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);

  // Background subtle pill
  ctx.fillStyle = 'rgba(18, 18, 24, 0.7)';
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(0, 0, width, height, 12);
  ctx.fill();
  ctx.stroke();

  // Primary Copyright Text
  ctx.font = 'bold 12.5px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillStyle = '#f4f4f5';
  ctx.fillText(FULL_COPYRIGHT_TEXT, 20, 28);

  // Subtitle / Legal Notice
  ctx.font = '500 9.5px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.fillStyle = '#a1a1aa';
  ctx.fillText(SHORT_COPYRIGHT_NOTICE, 20, 48);
}

/**
 * Initializes watchdog protecting the copyright container element.
 */
export function setupAntiTamperGuard(
  targetElement: HTMLElement | null,
  restoreCallback: () => void
): () => void {
  if (!targetElement || typeof window === 'undefined') return () => {};

  let observer: MutationObserver | null = null;
  try {
    observer = new MutationObserver((mutations) => {
      let needsRestore = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const removedNode of Array.from(mutation.removedNodes)) {
            if (removedNode === targetElement || (removedNode as HTMLElement).contains?.(targetElement)) {
              needsRestore = true;
              break;
            }
          }
        }

        if (mutation.type === 'attributes' && mutation.target === targetElement) {
          const style = window.getComputedStyle(targetElement);
          if (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            parseFloat(style.opacity || '1') < 0.1
          ) {
            needsRestore = true;
          }
        }
      }

      if (needsRestore) {
        targetElement.style.display = 'flex';
        targetElement.style.visibility = 'visible';
        targetElement.style.opacity = '1';
        restoreCallback();
      }
    });

    const parentNode = targetElement.parentNode || document.body;
    observer.observe(parentNode, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
  } catch {
    // Observer initialized
  }

  const intervalId = setInterval(() => {
    if (!document.body.contains(targetElement)) {
      restoreCallback();
    }
  }, 5000);


  return () => {
    if (observer) observer.disconnect();
    clearInterval(intervalId);
  };
}

/**
 * Initializes global browser protection against right-click inspect and DevTools hotkeys
 */
export function initGlobalAntiInspectProtection(): () => void {
  if (typeof window === 'undefined') return () => {};

  const preventDefaultHandler = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
    const altOrOpt = e.altKey;
    const shift = e.shiftKey;
    const key = e.key.toLowerCase();
    const keyCode = e.keyCode;

    // Block F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Ctrl+Shift+K, Ctrl+U, Ctrl+S
    if (
      keyCode === 123 ||
      key === 'f12' ||
      (ctrlOrCmd && (shift || altOrOpt) && (key === 'i' || key === 'j' || key === 'c' || key === 'k')) ||
      (ctrlOrCmd && key === 'u') ||
      (ctrlOrCmd && key === 's')
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };

  window.addEventListener('contextmenu', preventDefaultHandler, true);
  window.addEventListener('keydown', handleKeyDown, true);

  return () => {
    window.removeEventListener('contextmenu', preventDefaultHandler, true);
    window.removeEventListener('keydown', handleKeyDown, true);
  };
}

